import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, forkJoin, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { DepartmentService, IBaseDepartment } from '../department.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema } from '../file-transfer-services/interfaces';
import { IUser, UserService } from '../user.service';
import { MesDepartmentValidationService } from '../Validation/mesDepartment.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Departments';

const MesDepartmentSchema: IFileDataSchema<IMesDepartmentExportData> = {
	name: sheetName,
	properties: {
		alias: { type: 'string', required: true, fileKey: 'Alias' },
		name: { type: 'string', required: true, fileKey: 'Name' },
		isActive: { type: 'boolean', required: true, fileKey: 'IsActive' },
		shiftApprovers: { type: { array: 'string' }, required: true, fileKey: 'ShiftApprovers' },
	},
};

export interface MesDepartmentImportContext {
	iamUsers: IUser[];
	departments: IBaseDepartment[];
}

@Injectable({
	providedIn: 'root',
})
export class MesDepartmentTransferService implements ITransferService<IBaseDepartment> {
	constructor(
		private departmentService: DepartmentService,
		private userService: UserService,
		private mesDepartmentValidationService: MesDepartmentValidationService,
		private excelFileTransfer: ExcelFileTransfer<IMesDepartmentExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => false;
	public getTemplate = (): void => {
		throw new Error('Departments offer no import template; check hasTemplate() before calling getTemplate().');
	};

	public exportData(user: IBaseDepartment[]) {
		return from(user).pipe(
			combineAllWith(this.userService.fetchUsers()),
			filterMap(([data, users]) => {
				const missingApprovers: string[] = [];
				const shiftApproverMatches = data.shiftApprovers.reduce((acc, curr) => {
					const user = users.find((user) => user.id === curr);
					if (user) {
						acc.push(user.email);
					} else {
						missingApprovers.push(curr);
					}
					return acc;
				}, [] as string[]);

				if (missingApprovers.length > 0) {
					console.warn(`Warning: Some shift approvers for department ${data.alias} were not found: ${missingApprovers.join(', ')}`);
				}

				return {
					alias: data.alias,
					name: data.name,
					isActive: data.isActive,
					shiftApprovers: shiftApproverMatches,
				} as IMesDepartmentExportData;
			}),
			toArray(),
			mergeMap((data) => {
				return this.excelFileTransfer.exportData(sheetName, data, MesDepartmentSchema);
			}),
			map(() => user), // Return the original user array after export
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	importData(file: File | File[], overwrite: boolean = false) {
		const ctx: MesDepartmentImportContext = {
			iamUsers: [],
			departments: [],
		};

		const ImportErrors: ImportErrorTypes[] = [];
		if (!Array.isArray(file)) {
			// If a single file is provided, we convert it to an array
			file = [file];
		}

		const ob = readFiles(file, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				forkJoin([this.userService.fetchUsers(), this.departmentService.getDepartments()]).pipe(
					map(([users, departments]) => {
						ctx.iamUsers = users;
						ctx.departments = departments.result;
						return {}; // We don't need to emit anything from this observable
					}),
				),
			),
			map(([department, _]) => department),
			filterMap(this.validateAndMapShiftApproverAliases(ctx, ImportErrors)),
			filterMap(this.validateDepartment(ImportErrors)),
			mergeMap(this.createOrUpdateDepartment(ctx, overwrite, ImportErrors)),
			toArray(),
			map((users) => {
				const successfulImports = users.map((u) => ({ Id: u.entity.alias, status: u.status }) as SuccessFullImport);

				return {
					successfulImports,
					errors: ImportErrors,
				} as ImportResponse;
			}),
		);

		return ob;
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IMesDepartmentExportData> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, MesDepartmentSchema, errorList).map((imported) => imported.data);

				if (errorList.length > 0) {
					// If there are errors, we throw an error with the list of errors
					errorList.forEach((e) => {
						ImportErrors.push({
							message: e.error,
							status: ImportStatus.ParsingError,
						} as FailedParse);
					});
				}

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	validateAndMapShiftApproverAliases(ctx: MesDepartmentImportContext, ImportErrors: ImportErrorTypes[]) {
		return (department: IMesDepartmentExportData): IBaseDepartment | undefined => {
			const missingApprovers: string[] = [];
			const shiftApproverMatches = department.shiftApprovers.reduce((acc, curr) => {
				const user = ctx.iamUsers.find((user) => user.email.toLowerCase() === curr.toLowerCase());
				if (user) {
					acc.push(user.id);
				} else {
					missingApprovers.push(curr);
				}
				return acc;
			}, [] as string[]);

			if (missingApprovers.length > 0) {
				const message = this.translator.instant('mes.administration.department.import.errors.missing-shift-approvers', { missing: missingApprovers.join(', ') });
				const error: FailedValidationImport = {
					Id: `${department.alias}`,
					status: ImportStatus.ValidationError,
					errors: {
						shiftApprovers: { required: message },
					},
				};

				ImportErrors.push(error);
				return undefined;
			}

			return {
				alias: department.alias,
				name: department.name,
				isActive: department.isActive,
				shiftApprovers: shiftApproverMatches,
			} as IBaseDepartment;
		};
	}

	validateDepartment(ImportErrors: ImportErrorTypes[]) {
		return (department: IBaseDepartment): IBaseDepartment | undefined => {
			// Validate user
			const validationErrors = this.mesDepartmentValidationService.validateUser(department, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: `${department.alias}`,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return department;
		};
	}

	createOrUpdateDepartment(ctx: MesDepartmentImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (department: IBaseDepartment) => {
			const existingDepartment = ctx.departments.find((e) => e.alias === department.alias);
			if (existingDepartment) {
				if (!overwrite) {
					const message = this.translator.instant('mes.administration.department.import.errors.exists', { alias: department.alias });
					// Do not overwrite existing department
					ImportErrors.push({
						Id: department.alias,
						status: ImportStatus.Skipped,
						message,
					});
					return of();
				}
				// preserve the id of the existing department
				department.id = existingDepartment.id;
				// Update existing department
				return this.departmentService.updateDepartment(department).pipe(
					map(() => ({
						entity: department,
						status: ImportStatus.SuccessUpdate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.department.import.errors.update-failed', { alias: department.alias, msg: error.message });
						ImportErrors.push({
							Id: department.alias,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			} else {
				// Create new department
				return this.departmentService.createDepartment(department).pipe(
					map(() => ({
						entity: department,
						status: ImportStatus.SuccessCreate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.department.import.errors.create-failed', { alias: department.alias, msg: error.message });
						ImportErrors.push({
							Id: department.alias,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			}
		};
	}
}

export interface IMesDepartmentExportData {
	alias: string;
	name: string;
	isActive: boolean;
	shiftApprovers: string[];
}
